import { useCallback, useEffect, useRef, useState } from "react";
import { SortColumn } from "react-data-grid";

import { CompleteBlueIcon } from "../../../../../assets";
import { ResourcePermissionConstants, WorkflowOrganisationAccessLevel } from "../../../../../constants";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { CursorChangeProps, ResultType, TaskPanelData } from "../../../../../models";
import {
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksRequest,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { getTaskBoard } from "../../../../../service/query/QueryService.full";
import { FilterGroups, ResultData, Status } from "../../../../../service/Shared";
import { useDeepLinkedTask } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import { flattenObject, hasWorkflowOrganisationAccessLevel, useUpdateEffect } from "../../../../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  DataGridCursorDataLoadEventHandler,
  Toast,
} from "../../../../../widget";
import { AvatarCellFormatter } from "../../../../../widget/data/DataGrid/components/CellFormatters";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";

interface UseDealUpcomingTasksReturnData {
  columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<unknown>)[];
  defaultSortingCriteria: SortColumn[];
  refreshTasks: boolean;
  handleRefresh: () => void;
  handleAddTask: () => void;
  isUpcomingTasksLoading: boolean;
  upcomingTasksOnChange: DataGridCursorDataLoadEventHandler;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  handleCloseWorkflowTaskPanel: () => void;
  // Create-mode task type: true when the task about to be created is standalone (no workflow step).
  createTaskIsGeneral: boolean;
  showNewTaskModal: boolean;
  // True while the workflow deciding whether a workflow task is on offer is still loading.
  isWorkflowLoading: boolean;
  handleCloseNewTaskModal: () => void;
  handleNewTask: (isGeneral: boolean) => void;
  // True when at least one task type is creatable here, so the header can offer "Add task" at all.
  canAddTask: boolean;
  // General tasks are standalone, so this holds whether or not the deal has a workflow.
  canCreateGeneralTask: boolean;
  // A workflow task needs a step to hang off, so this additionally requires an applied workflow.
  canCreateWorkflowTask: boolean;
  selectedCompleteTask: TaskPanelData | undefined;
  showCompleteTaskModal: boolean;
  handleCloseCompleteTaskModal: () => void;
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Task",
    key: "taskLink",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    filterable: false,
    minWidth: 260,
    sortable: false,
  },
  {
    name: "Assigned to",
    key: "avatar",
    dataType: "string",
    formatter: "custom",
    alignment: "center",
    customCellFormatter: AvatarCellFormatter,
    filterable: false,
    minWidth: 140,
    sortable: false,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
    filterable: false,
    minWidth: 140,
    sortable: false,
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
    filterable: false,
    minWidth: 160,
    sortable: false,
  },
  {
    name: "Complete",
    key: "completeIcon",
    dataType: "boolean",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 124,
  },
];

export const useDealUpcomingTasks = (
  workflowUuid?: string | null,
  dealUuid?: string,
  canEdit = false
): UseDealUpcomingTasksReturnData => {
  const { currentOrganisationUuid, hasPermission } = useAuth();

  const [workflow, setWorkflow] = useState<GetTaskBoardResponse["results"][number]>();
  /*
    The workflow uuid whose read has finished. This answers "has it loaded yet", which the absence of a
    workflow cannot: a failed or empty read leaves none but is still an answer, and waiting on
    `workflow === undefined` would leave the gates loading forever. Holding the uuid rather than a bare flag
    also marks them as loading in the same render the deal's workflow changes, not one effect later.
  */
  const [settledWorkflowUuid, setSettledWorkflowUuid] = useState<string | null | undefined>(undefined);

  /*
    The stored workflow is this deal's only when it identifies itself as such. Matching on the value's own
    uuid rather than on which read last settled is what makes that airtight: a failed read for a new deal
    settles without replacing the workflow, so a settled-uuid check would hand back the previous deal's one
    — and clearing it in an effect instead would still leave the render that introduced the mismatch. The
    create gates, the Step picker and the card all read this, so none of them can be given a stale workflow.
  */
  const currentWorkflow = workflow?.uuid === workflowUuid ? workflow : undefined;
  // Id of the latest workflow load, so a response for a deal the page has moved on from is ignored.
  const workflowRequestIdRef = useRef(0);
  /*
    Uuid of the workflow currently held, for the async paths — they close over the render that started them,
    so they cannot read it off state. Only used to tell a first read from a refresh when one fails.
  */
  const loadedWorkflowUuidRef = useRef<string | undefined>(undefined);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [selectedCompleteTask, setSelectedCompleteTask] = useState<TaskPanelData>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  // General is the deal's long-standing default, and the only type creatable without a workflow.
  const [createTaskIsGeneral, setCreateTaskIsGeneral] = useState(true);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);
  const [isUpcomingTasksLoading, setIsUpcomingTasksLoading] = useState(true);
  const [refreshTasks, setRefreshTasks] = useState(false);

  /*
    Mirror the API's two create gates so the offered types match what createWorkflowTask will accept:
    - general task — createResource on the acting org, and no workflow needed since it is standalone;
    - workflow-step task — createWorkflowElements on the workflow, which the ACL grants to Contributor and
      above, plus a workflow applied to this deal to take the step from.
    Both are also held behind canEdit: a deal that is no longer editable takes no new tasks either.
  */
  const canCreateGeneralTask = canEdit && hasPermission(ResourcePermissionConstants.CREATE_RESOURCE);
  const workflowAccessLevel = currentWorkflow?.organisations?.find(
    (organisation) => organisation.organisationUuid === currentOrganisationUuid
  )?.accessLevel as WorkflowOrganisationAccessLevel | undefined;
  const canCreateWorkflowTask =
    canEdit &&
    Boolean(workflowUuid) &&
    hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.Contributor, workflowAccessLevel);
  const canAddTask = canCreateGeneralTask || canCreateWorkflowTask;

  /*
    The deal knows it has a workflow before the workflow itself arrives, and the workflow-task gate is read
    off that response — so until the read settles the second type is neither offered nor ruled out. A read
    that fails still settles, so a broken workflow lookup cannot block creating a general task.
  */
  const isWorkflowLoading = Boolean(workflowUuid) && settledWorkflowUuid !== workflowUuid;

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  /*
    The grid mounts before the deal has loaded, and the first search runs unscoped-and-empty. Reload once
    either half of the scope arrives, or the card would sit empty until something else refreshed it.
  */
  useUpdateEffect(() => setRefreshTasks((prev) => !prev), [workflowUuid, dealUuid]);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(true);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleCloseNewTaskModal = useCallback((): void => setShowNewTaskModal(false), []);

  /*
    With a workflow applied a task can be either type, so ask which before opening the overlay — including
    while the workflow is still loading, since the answer may yet turn out to be "both" and the modal holds
    Confirm until it settles. With only one type creatable there is nothing to choose between, so go
    straight to it rather than showing a single-option modal.
  */
  const handleAddTask = useCallback((): void => {
    setSelectedPanelTask(undefined);

    if (canCreateGeneralTask && (canCreateWorkflowTask || isWorkflowLoading)) {
      setShowNewTaskModal(true);
      return;
    }

    setCreateTaskIsGeneral(canCreateGeneralTask);
    setShowWorkflowTaskPanel(true);
  }, [canCreateGeneralTask, canCreateWorkflowTask, isWorkflowLoading]);

  // Confirming the type opens the overlay in create mode for whichever was chosen.
  const handleNewTask = useCallback((isGeneral: boolean): void => {
    setShowNewTaskModal(false);
    setSelectedPanelTask(undefined);
    setCreateTaskIsGeneral(isGeneral);
    setShowWorkflowTaskPanel(true);
  }, []);

  /*
    A notification for one of this deal's tasks links here naming the task in ?taskUuid= (KAN-9609). A deal
    has no tasks tab, so this page is where its general tasks are reached — open the named one on arrival
    rather than leaving the user to find it in the upcoming-tasks card, which only lists the next few.
  */
  const deepLinkedTask = useDeepLinkedTask();
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSelectedPanelTask(deepLinkedTask);
    setShowWorkflowTaskPanel(true);
  }, [deepLinkedTask]);

  const handleCloseCompleteTaskModal = useCallback((): void => {
    setSelectedCompleteTask(undefined);
    setShowCompleteTaskModal(false);
  }, []);

  const formatUpcomingTasksData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((task) => {
        const row = flattenObject(task) as ResultData & { avatar: { fullName?: string } };

        row.avatar = { fullName: task.assignee?.name };
        row.taskLink = {
          text: task.name,
          action: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (task.status !== TaskConstants.TASK_STATUS_UNASSIGNED) {
          row.completeIcon = {
            action: () => {
              setSelectedCompleteTask({
                deadlineDate: task.deadlineDate,
                isMandatory: task.isMandatory,
                isMilestone: task.isMilestone,
                keyDate: task.keyDate,
                status: task.status,
                repeat: task.repeat,
                description: task.description,
                name: task.name,
                visibility: [],
                assignedToUserUuid: task.assignee?.uuid ?? null,
                assignedToUserName: task.assignee?.name ?? null,
                ownerOrganisationUuid: task.ownerOrganisation.uuid,
                relateToActivity: task.objectType === "Activity",
                activityUuid: task.objectType === "Activity" ? task.objectUuid : null,
                objectType: task.objectType,
                objectUuid: task.objectUuid,
                stepUuid: task.step?.uuid ?? null,
                phase: task.step?.phase ?? null,
                uuid: task.uuid,
                rowVersion: task.rowVersion,
                resolution: task.resolution,
                reminderDate: task.reminderDate,
                reminderType: task.reminderType,
                addReminder: task.reminderDate != null,
              });
              setShowCompleteTaskModal(true);
            },
            icon: <CompleteBlueIcon width={20} height={20} strokeWidth={1.5} />,
          } as IconCellFormatterData;
        }

        return row;
      }) || [],
    []
  );

  const upcomingTasksOnChange: DataGridCursorDataLoadEventHandler = async ({ paging }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize || 5,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    // Nothing to scope the search to yet.
    if (!workflowUuid && !dealUuid) {
      setIsUpcomingTasksLoading(false);
      return data;
    }

    setIsUpcomingTasksLoading(true);

    /*
      Incomplete tasks belonging to this deal, scoped by whichever routes exist. With a workflow a task
      qualifies either through it or by hanging off the deal directly (general tasks have no workflow
      step), so those two go in an "or" group ANDed with the status. With no workflow the deal's own tasks
      are the whole scope, so objectUuid goes in as a plain criterion rather than a one-armed group.
    */
    const request: SearchWorkflowTasksRequest & FilterGroups<SearchWorkflowTasksRequest["filter"]> = {
      paging: {
        limit: 5,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "results.deadlineDate", direction: "asc" }],
      filter: {
        results: {
          status: {
            operator: "neq",
            value: TaskConstants.TASK_STATUS_COMPLETE,
          },
          ...(!workflowUuid && dealUuid ? { objectUuid: { operator: "eq", value: dealUuid } } : {}),
        },
      },
      ...(workflowUuid
        ? {
            filterGroups: [
              {
                operator: "or",
                filter: {
                  results: {
                    ...(dealUuid ? { objectUuid: { operator: "eq", value: dealUuid } } : {}),
                    workflowUuid: { operator: "eq", value: workflowUuid },
                  },
                },
              },
            ],
          }
        : {}),
    };

    await searchWorkflowTasks(request)
      .then((response) => {
        data = {
          resultData: formatUpcomingTasksData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 5,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .finally(() => {
        setIsUpcomingTasksLoading(false);
      });

    return data;
  };

  useEffect(() => {
    // Monotonic stamp: a response for a deal the page has moved on from must not be applied. Bumped before
    // the early return too, so losing the workflow supersedes a read that is still in flight for it.
    workflowRequestIdRef.current += 1;
    const requestId = workflowRequestIdRef.current;

    if (!workflowUuid) {
      setWorkflow(undefined);
      loadedWorkflowUuidRef.current = undefined;
      setSettledWorkflowUuid(workflowUuid);
      return;
    }

    /*
      A refresh that fails still leaves the workflow read moments ago, and that stays the best data there
      is — dropping it would revoke the workflow-task option and blank the Step picker over a background
      read that hiccuped, which is worse than serving it a little stale. So only claim the option is gone
      when there is genuinely no workflow to offer it from; otherwise say it may be out of date.
    */
    const reportReadFailure = (): void => {
      Toast.error({
        message:
          loadedWorkflowUuidRef.current === workflowUuid
            ? "Unable to refresh the workflow for this deal, so its tasks may be out of date."
            : "Unable to load the workflow for this deal, so only general tasks can be added.",
      });
    };

    const fetchWorkflow = async (): Promise<void> => {
      const response = await getTaskBoard({
        showComplete: true,
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            uuid: {
              operator: "eq",
              value: workflowUuid,
            },
          },
        },
      });

      if (requestId !== workflowRequestIdRef.current) return;

      if (response.status === Status.Success && response.data) {
        const loadedWorkflow = response.data.results[0];
        setWorkflow(loadedWorkflow);
        loadedWorkflowUuidRef.current = loadedWorkflow?.uuid;
        return;
      }

      reportReadFailure();
    };

    fetchWorkflow()
      .catch(() => {
        if (requestId !== workflowRequestIdRef.current) return;
        reportReadFailure();
      })
      .finally(() => {
        // Settled either way: the gates must not sit waiting on a request that has already failed.
        if (requestId === workflowRequestIdRef.current) setSettledWorkflowUuid(workflowUuid);
      });
  }, [workflowUuid, refreshTasks]);

  return {
    columns,
    defaultSortingCriteria,
    refreshTasks,
    handleRefresh,
    handleAddTask,
    isUpcomingTasksLoading,
    upcomingTasksOnChange,
    workflow: currentWorkflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    createTaskIsGeneral,
    showNewTaskModal,
    isWorkflowLoading,
    handleCloseNewTaskModal,
    handleNewTask,
    canAddTask,
    canCreateGeneralTask,
    canCreateWorkflowTask,
    selectedCompleteTask,
    showCompleteTaskModal,
    handleCloseCompleteTaskModal,
  };
};
